import { Skeleton } from "@heroui/react";
import { useMemo, useState } from "react";

import { AvailableOrganizationAsyncInlineCommand } from "@/components/organization";
import { OrganizationLogo } from "@/components/organization/OrganizationLogo";
import { CurrentPlatformLogo } from "@/components/platform";
import {
  useCurrentOrganizationContext,
  useCurrentPlatformContext,
  useSwitchOrganization,
  useTourContext,
} from "@/contexts";
import { useCanSelectWholePlatform } from "@/hooks";
import { usePrefetchOnHover } from "@/hooks/common/usePrefetchOnHover";
import { useOrganizationPermissions } from "@/hooks/organization/useOrganizationPermissions";
import { useBreakpoint } from "@/hooks/userBreakPoint";
import { useT } from "@/i18n";
import { trackAction } from "@/lib/sentry";
import {
  cn,
  DialogTrigger,
  PopoverOrTray,
  Button,
  ButtonLink,
  IconPlus,
  IconButtonLink,
  IconSettings,
} from "@/ui";

import { GlobalSelectButton } from "../GlobalSelectButton";
import { useGetNavLinkPath } from "../NavLink";

export const OrganizationSelect = () => {
  const t = useT();
  const getNavLinkPath = useGetNavLinkPath();
  const tourContext = useTourContext();
  const switchOrganization = useSwitchOrganization();
  const isSmallScreen = useBreakpoint("md", "down");
  const canSelectWholePlatform = useCanSelectWholePlatform();
  const { canCreateOrganization, canViewOrganization, canUpdateOrganization } =
    useOrganizationPermissions();
  const canCreate = canCreateOrganization;
  // The settings page is reachable with `ORG:READ` alone (read-only without
  // `ORG:WRITE`), so the gear link opens on either key.
  const canOpenOrganizationSettings = canViewOrganization || canUpdateOrganization;

  const {
    organization: currentOrganization,
    isPlatformOnly,
    loading: currentOrganizationLoading,
  } = useCurrentOrganizationContext();
  const { platform: currentPlatform } = useCurrentPlatformContext();

  const [isOpen, setIsOpen] = useState(false);

  const excludeFederationIds = useMemo(
    () => (currentOrganization ? [currentOrganization.id] : []),
    [currentOrganization]
  );

  // The platform's data-owner federation first, then the legacy `owner` when it
  // is a different federation — mirroring the precedence the org context uses
  // when resolving the default organization on platform switch.
  const dataOwnerFederationId = currentPlatform?.dataOwner?.id ?? null;
  const ownerFederationId = currentPlatform?.owner?.id ?? null;
  const pinnedFederationIds = useMemo(
    () => [dataOwnerFederationId, ownerFederationId].filter((id) => id !== null),
    [dataOwnerFederationId, ownerFederationId]
  );

  const organizationSettingsHref = getNavLinkPath("organizationSettings");
  const createOrganizationHref = `${getNavLinkPath("organizations")}/new`;
  const prefetchOrganizationSettings = usePrefetchOnHover(organizationSettingsHref);
  const prefetchCreateOrganization = usePrefetchOnHover(createOrganizationHref);

  if (currentOrganizationLoading) {
    return <Skeleton className="w-[226px] h-[40px] rounded-medium" />;
  }

  // The select renders for a concrete organization, or for the platform-only
  // ("Whole platform") context — the latter is only ever reachable by users
  // holding at least one platform-level capability (possibly conferred through
  // a platform connection), so there is nothing to render otherwise.
  if (!currentOrganization && !isPlatformOnly) {
    return null;
  }

  const triggerTitle = isPlatformOnly
    ? t("organization.components.globalSelect.wholePlatform")
    : (currentOrganization?.name ?? "");

  const triggerIcon = isPlatformOnly ? (
    <CurrentPlatformLogo height={20} width={20} />
  ) : (
    currentOrganization && (
      <OrganizationLogo organization={currentOrganization} height={20} width={20} />
    )
  );

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      <GlobalSelectButton
        id={tourContext.ids.organizationSelect}
        className={cn({
          "max-w-[252px] min-w-[200px]": !isSmallScreen,
          "p-0": isSmallScreen,
        })}
        title={triggerTitle}
        subtitle={t("organization.components.globalSelect.label")}
        icon={triggerIcon}
        size={isSmallScreen ? "sm" : "md"}
      />
      <PopoverOrTray trayProps={{ size: "full" }} popoverProps={{ className: "p-3 w-[262px]" }}>
        {({ isTray }) => (
          <>
            <div className="flex gap-3 items-center w-full">
              <div className="flex items-center shrink-0">
                {isPlatformOnly ? (
                  <CurrentPlatformLogo height={24} width={24} />
                ) : (
                  currentOrganization && (
                    <OrganizationLogo organization={currentOrganization} height={24} width={24} />
                  )
                )}
              </div>
              <div className="flex flex-col flex-1 items-stretch">
                <span className="text-medium font-bold leading-[15px]! text-new-primary font-grotesk flex-1 text-wrap">
                  {triggerTitle}
                </span>
              </div>
              {!isPlatformOnly && currentOrganization && canOpenOrganizationSettings && (
                <div className="flex items-center">
                  <IconButtonLink
                    href={organizationSettingsHref}
                    onPress={() => setIsOpen(false)}
                    onHoverStart={prefetchOrganizationSettings}
                    onFocus={prefetchOrganizationSettings}
                    label={t("organization.actions.settings")}
                    size="sm"
                    hideTooltip={true}
                    icon={IconSettings}
                    className="text-new-primary"
                  />
                </div>
              )}
            </div>

            {/* "Whole platform" clears the organization, dropping into the
                platform-only context. Offered only when an org is currently
                selected and the user holds any capability on the platform's
                PLATFORM node — incl. read-only, connection-conferred access. */}
            {canSelectWholePlatform && !isPlatformOnly && (
              <Button
                variant="tertiary"
                color="brand"
                className="w-full mt-2.5 justify-start"
                startContent={<CurrentPlatformLogo height={18} width={18} />}
                onPress={() => {
                  trackAction("dashboard.header.organization.change", {
                    fromOrganizationId: currentOrganization?.id ?? null,
                    toOrganizationId: null,
                  });
                  switchOrganization(null);
                  setIsOpen(false);
                }}
              >
                {t("organization.components.globalSelect.wholePlatform")}
              </Button>
            )}

            <AvailableOrganizationAsyncInlineCommand
              excludeFederationIds={excludeFederationIds}
              pinnedFederationIds={pinnedFederationIds}
              autoFocus={!isTray}
              className="mt-2.5"
              menuProps={{
                style: !isTray
                  ? {
                      height: "140px",
                      maxHeight: "140px",
                      minHeight: "140px",
                    }
                  : undefined,
                className: isTray ? "flex-[1_1_0] overflow-y-auto" : undefined,
              }}
              onOptionAction={(option) => {
                if (!option) return;
                trackAction("dashboard.header.organization.change", {
                  fromOrganizationId: currentOrganization?.id ?? null,
                  toOrganizationId: option.id,
                });
                switchOrganization(option.id);
                setIsOpen(false);
              }}
            />

            {canCreate && (
              <ButtonLink
                href={createOrganizationHref}
                startIcon={IconPlus}
                variant="tertiary"
                color="brand"
                onHoverStart={prefetchCreateOrganization}
                onFocus={prefetchCreateOrganization}
                onPress={() => {
                  setIsOpen(false);
                }}
                className="w-full mt-2.5"
              >
                {t("organization.actions.create")}
              </ButtonLink>
            )}
          </>
        )}
      </PopoverOrTray>
    </DialogTrigger>
  );
};
